import React, { useEffect, useState } from "react";
import { MenuOption, Table, TableColumn, TableMenu } from "../components/Table";
import { PAGINATION_PAGE_NUMBER } from "../constants";
import { FaArrowLeft } from "react-icons/fa";
import Vendor from "../models/Vendor";
import { useModal } from "../context/ConfirmModalContext";
import { errorMessage, sendRequest } from "../utils/api";
import { useToast } from "../context/ToastContext";
import FullPageSpinner from "../components/FullPageSpinner";
import { initAllStores } from "../utils/cache";
import { isEmptyObject } from "../utils/helper-function";
import { v4 as uuidv4 } from "uuid";


const columns: TableColumn[] = [
  {
    key: "name", label: "Name",
    getClassName: () => "capitalize",
    responsive: "md"
  },
  {
    key: "service", label: "Service",
    getClassName: () => "capitalize",
  },
  {
    key: "phone", label: "phone",
    getClassName: () => "capitalize",
  },

];

const VendorsPage = () => {

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);


  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [isEdit, setIsEdit] = useState(true);
  const [loading, setLoading] = useState(false);

  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");

  const getVendors = async () => {
    setLoading(true);
    try {
      const cache = await initAllStores();

      //TODO: Check first if in cache before fetching from server.

      let vendors: Vendor[] = await cache.getAllBy(
        "vendors", {}, "name", "asc"
      );

      if (isEmptyObject(vendors)) {
        vendors = await sendRequest("GET", "/vendors");
      }

      setVendors(vendors);

    } catch (err) {
      console.error("Failed to load items from cache:", err);
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    getVendors();
  }, []);


  const resetForm = () => {
    setName("");
    setPhone("");
    setService("");
  }

  const createVendor = async () => {
    setLoading(true);

    try {
      const cache = await initAllStores();

      const id = uuidv4();
      const data = { id, name, phone, service };
      await sendRequest("POST", "/vendors", data);
      setVendors(prev => [data as Vendor, ...prev,]);
      await cache.saveOne("vendors", data);
      resetForm();
      setShowModal(false);
      showToast("Vendor Added Successfully.", { type: "success" });
    } catch {
      showToast("Error occur adding vendor.", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  const updateVendor = async () => {
    setLoading(true);
    try {
      const cache = await initAllStores();

      const data = { name, phone, service };

      await sendRequest("PUT", `/vendors/${id}/update`, data);
      setVendors((vendors) =>
        vendors.map((vd) =>
          vd.id === id
            ? {
              ...vd,
              ...data,
            }
            : vd
        )
      );

      await cache.saveOne("vendors", { ...data, id });
      resetForm();
      setShowModal(false);
      showToast("Vendor Updated Successfully.", { type: "success" });
    } catch (err: any) {
      console.log(err)
      showToast("Error occur updating vendor.", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  const confirmAction = async () => {
    const missingFields: string[] = [];
    if (!name) missingFields.push("Name");
    if (!service) missingFields.push("Service");
    if (!phone) missingFields.push("Phone");

    if (missingFields.length > 0) {
      return openModal(
        "Missing Fields",
        `Please fill in: ${missingFields.join(", ")}`,
        closeModal, false, "Close"
      );
    }

    openModal(isEdit ? "Confirm Vendor Update" : "Add New Vendor",
      `Are you sure you want to ${isEdit ? "Update" : "Add"} this vendor?`,
      () => {
        if (isEdit) updateVendor()
        else createVendor()
      },
      true, "Confirm", "Cancel");
  };

  const deleteVendor = async (id: string) => {
    setLoading(true);
    try {
      const cache = await initAllStores();
        await sendRequest("DELETE", `/vendors/${id}/delete`);

      await cache.deleteOne("vendors", id);

      setVendors((prevItems) => prevItems.filter((item) => item.id !== id));
     showToast("Vendor Deleted Successfully", { type: "success" });

    } catch (error: any) {
      console.log(error.status);
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor.name.toLowerCase().includes(search.toLowerCase()) ||
    vendor.service.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <FullPageSpinner show={loading} />
      {/* Header */}
      <div className="flex justify-between items-center mb-6">

        {/* Left Section (Back + Title) */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            <span className="text-gray-700 text-lg" >{FaArrowLeft({})}</span>
          </button>

          <h1 className="text-2xl font-semibold">Vendors</h1>
        </div>

        {/* Right Section (Add Vendor) */}
        <button
          onClick={() => {
            setIsEdit(false);
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition"
        >
          + Add Vendor
        </button>

      </div>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search vendor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-1/3 px-4 py-2 border rounded-xl"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow overflow-x-auto">
        <Table
          columns={columns}
          data={filteredVendors}
          rowsPerPage={PAGINATION_PAGE_NUMBER}
          tableMenu={(row: any, closeMenu) => (
            <TableMenu
              row={row}
              options={[
                {
                  label: "Details",
                  onClick: (v: Vendor) => {
                    setShowModal(true);
                    setIsEdit(true);
                    setId(v.id);
                    setName(v.name);
                    setService(v.service);
                    setPhone(v.phone);
                  },
                },
                {
                  label: "Delete",
                  color: "text-red-500",
                  onClick: (v: Vendor) => {
                    return openModal(
                      "Confirm Delete",
                      "Are you sure you want to delete this vendor" +
                      "? This action cannot be undone.",
                      () => {
                        closeModal();
                        deleteVendor(v.id);
                      },
                      true, "Delete", "Cancel", "red"
                    );
                  },
                },

              ].filter(Boolean) as MenuOption[]}
              closeMenu={closeMenu}
            />
          )}
        />
      </div>

      {/* Add Vendor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
            <h2 className="text-xl font-semibold mb-4">{isEdit ? "Update" : "Add"} Vendor</h2>

            <div
              // onSubmit={(e) => {
              //   e.preventDefault();
              //   const formData = new FormData(e.target);
              //   const newVendor = {
              //     id: vendors.length + 1,
              //     business_name: formData.get("name"),
              //     email: formData.get("service"),
              //     phone: formData.get("phone"),
              //     balance: 0,
              //     status: "Active",
              //   };
              //   setVendors([...vendors, newVendor]);
              //   setShowModal(false);
              // }}
              className="space-y-4"
            >
              <input
                name="name"
                placeholder="Name"
                onChange={(e) => setName(e.target.value)}
                value={name}
                required
                className="w-full px-4 py-2 border rounded-xl"
              />
              <input
                name="phone"
                placeholder="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-2 border rounded-xl"
                required
              />
              <input
                name="service"
                placeholder="Service Provided"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="w-full px-4 py-2 border rounded-xl"
                required
              />

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="px-4 py-2 rounded-xl border"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className="bg-blue-600 text-white px-4 py-2 rounded-xl"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsPage;